// Public API. One entry point: loadViewer(opts).

import { IncrementalPackageCompiler } from "@archlang/engine";
import { parseCached } from "@archlang/engine";
import { discoverPackageFromVfs } from "./load-package.js";
import { seedFromDiscovered, withDiscoveryDiagnostics } from "./incremental-viewer.js";
import { withStubs, type ViewerState } from "./build-state.js";
import { STDLIB_FILES } from "./stdlib-data.js";
import type { Vfs } from "./vfs.js";

export type { Vfs } from "./vfs.js";
export { isMarkdownPath } from "./vfs.js";
// Also reachable as `@archlang/viewer-core/workspace-file` — a subpath that
// pulls no stdlib bundle, for the server and other non-viewer consumers.
export {
  ARCH_EXT,
  ARCHSPACE_EXT,
  MD_EXT,
  PACKAGE_MANIFEST,
  basenameOf,
  dirOf,
  isWorkspaceFile,
  hasWorkspaceExt,
  canonicalBasename,
  canonicalPath,
  hasStem,
  isLegalSegment,
  isManifestPath,
  isWorkspacePath,
  resolveNewPath,
  hasAncestorManifest,
  manifestSeed,
  newFileSeed,
} from "./workspace-file.js";
export type { LoadedPackage } from "./load-package.js";
export {
  discoverRootsFromVfs,
  ownerOfPath,
  spaceOfPath,
  type VfsRoot,
  type VfsDiscovery,
} from "./discover-roots.js";
export type { ViewerState } from "./build-state.js";
export { buildViewerState, withStubs, type ResolvedPackageInput } from "./build-state.js";
export { IncrementalViewer, type IncrementalUpdateOptions, type IncrementalResult } from "./incremental-viewer.js";
export {
  serializeModel,
  createIncrementalSerializer,
  serializeEdges,
  callWireEdges,
  isCallWireEdge,
  serializeDiagnostics,
  type WireArchitectureModel,
  type WireDependencyEdge,
  type WireDiagnostic,
  type WireModule,
  type WireSurface,
  type WireInterface,
  type WireFieldEntry,
  type WireAspectEntry,
  type WireProcess,
  type WireSubprocess,
  type WireView,
  type WireTypeDef,
  type WireTypeFieldEntry,
  type WireTypeAspectEntry,
  type WireMarkdownDocument,
  type WireBacklink,
} from "./serialize.js";

export interface LoadViewerOptions {
  readonly sources: Vfs;
  readonly rootPath?: string;
  readonly stdlib?: Vfs;
  readonly stdlibPath?: string;
}

export type ViewerLoadResult = ViewerState;

/**
 * One-shot compile of a VFS workspace → ViewerState. Engine-backed: discovery
 * (roots/deps/spaces) feeds an IncrementalPackageCompiler, whose `state()` is
 * the result — byte-equivalent to the old cold `buildViewerState` (the
 * equivalence-test oracle), but on the single incremental code path. A caller
 * that edits repeatedly should hold an `IncrementalViewer` instead, to reuse
 * work across edits.
 */
export async function loadViewer(opts: LoadViewerOptions): Promise<ViewerLoadResult> {
  const discovered = await discoverPackageFromVfs({
    userVfs: opts.sources,
    userRoot: opts.rootPath ?? "/",
    stdlibVfs: opts.stdlib ?? STDLIB_FILES,
    stdlibRoot: opts.stdlibPath ?? "/__stdlib__",
  });
  const ipc = new IncrementalPackageCompiler(parseCached);
  seedFromDiscovered(ipc, discovered);
  return withStubs(withDiscoveryDiagnostics(ipc.state(), discovered.diagnostics));
}

export { STDLIB_FILES };
export { generateWidgetsCss, _resetWidgetsCssCache } from "./widgets-css.js";
export {
  swimlaneExportOptions,
  resolveExpandedSubprocesses,
  type SwimlaneExportOptions,
  type SwimlaneExportAuxEdge,
} from "./process-swimlane-export.js";
export {
  processQualifiedName,
  resolveProcessByName,
  matchProcess,
  freshBpmnScaffold,
  renderProcessSwimlaneSvg,
  renderProcessBpmnSvg,
  renderProcessBpmnDiffSvg,
  bpmnOwnerColor,
} from "./process-render.js";
export {
  resolveFlowProcess,
  resolveFlowViewByName,
  bpmnLayoutForFlowView,
  computeBpmnViewLints,
  renderFlowViewSvg,
  type FlowViewMode,
  type ResolvedFlowView,
  type FlowViewResolution,
  type AnchoredBpmnLint,
} from "./process-view-render.js";
export {
  renderViewSvg,
  renderTabularViewSvg,
  renderIndexViewSvg,
  isTabularViewName,
  isIndexViewName,
  lookupDeclaredView,
  tabularRepresentationOf,
  type TabularRepresentation,
} from "./view-tabular-render.js";
export {
  spelledViewName,
  viewRepresentation,
  type ViewRepresentation,
} from "./view-lookup.js";
export {
  buildNavTree,
  EMPTY_NAV_TREE,
  type NavLocation,
  type NavNode,
  type NavNodeKind,
  type NavTree,
} from "./nav-tree.js";
export {
  buildEntityDetails,
  type BuildDetailsOptions,
  type DetailBacklink,
  type DetailCall,
  type DetailConnection,
  type DetailInterface,
  type DetailKind,
  type DetailLifecycle,
  type DetailPair,
  type DetailRef,
  type EntityDetails,
} from "./entity-details.js";
export { aspectColorFor } from "./aspect-color.js";
export { buildReplicateOption, replicateKeyOf, buildEngineOption, engineKeyOf } from "./process-replicate.js";
export {
  boardLifecyclePaint,
  scrubRejection,
  renderBoardSvgFromState,
  type BoardRenderOptions,
  type BoardRenderResult,
} from "./board-render.js";
export {
  resolveLifecyclePaint,
  resolveLifecycleDeltaAt,
  type LifecycleDelta,
  type LifecyclePaintMap,
} from "./lifecycle-paint.js";
export {
  effectivePlane,
  planeClause,
  planeDefaultCtx,
  planePlan,
  planeRejection,
} from "./plane-projection.js";
